import { sql } from 'drizzle-orm';
import {
  pgTable,
  index,
  jsonb,
  timestamp,
  varchar,
  text,
  integer,
  decimal,
  boolean,
  uuid,
  primaryKey
} from "drizzle-orm/pg-core";
import { learningExamples } from './ai';
import { claims } from './claims';
import { documents } from './documents';
import { checkProcessingStageEnum, estimateTypeEnum, expenseTypeEnum, feeBasisEnum, feeSourceEnum, feeTypeEnum, invoiceStatusEnum, offerTypeEnum, paymentProcessingStatusEnum, paymentStatusEnum, paymentTypeEnum, policyCoverageEnum, timeEntryTypeEnum } from './enums';
import { organizations } from './organizations';
import { users } from './users';

// Estimates table
export const estimates = pgTable("estimates", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "cascade" }).notNull(),
  type: estimateTypeEnum("type").notNull(),
  vendor: varchar("vendor"), // Company/person who provided estimate
  amount: decimal("amount", { precision: 12, scale: 2 }).notNull(),
  version: integer("version").default(1),
  lineItems: jsonb("line_items"), // Detailed breakdown
  
  // Coverage-specific RCV and depreciation tracking
  coverageBreakdown: jsonb("coverage_breakdown"), // Detailed breakdown by coverage type
  totalRcv: decimal("total_rcv", { precision: 12, scale: 2 }), // Total Replacement Cost Value
  totalRecoverableDepreciation: decimal("total_recoverable_depreciation", { precision: 12, scale: 2 }),
  totalNonRecoverableDepreciation: decimal("total_non_recoverable_depreciation", { precision: 12, scale: 2 }),
  totalAcv: decimal("total_acv", { precision: 12, scale: 2 }), // Actual Cash Value (RCV - Depreciation)
  
  // Coverage A - Dwelling
  dwellingRcv: decimal("dwelling_rcv", { precision: 12, scale: 2 }),
  dwellingRecoverableDepreciation: decimal("dwelling_recoverable_depreciation", { precision: 12, scale: 2 }),
  dwellingNonRecoverableDepreciation: decimal("dwelling_non_recoverable_depreciation", { precision: 12, scale: 2 }),
  
  // Coverage B - Other Structures
  otherStructuresRcv: decimal("other_structures_rcv", { precision: 12, scale: 2 }),
  otherStructuresRecoverableDepreciation: decimal("other_structures_recoverable_depreciation", { precision: 12, scale: 2 }),
  otherStructuresNonRecoverableDepreciation: decimal("other_structures_non_recoverable_depreciation", { precision: 12, scale: 2 }),
  
  // Coverage C - Personal Property
  personalPropertyRcv: decimal("personal_property_rcv", { precision: 12, scale: 2 }),
  personalPropertyRecoverableDepreciation: decimal("personal_property_recoverable_depreciation", { precision: 12, scale: 2 }),
  personalPropertyNonRecoverableDepreciation: decimal("personal_property_non_recoverable_depreciation", { precision: 12, scale: 2 }),
  
  // Coverage D - Loss of Use/ALE
  lossOfUseRcv: decimal("loss_of_use_rcv", { precision: 12, scale: 2 }),
  
  documentId: uuid("document_id").references(() => documents.id),
  notes: text("notes"),
  receivedDate: timestamp("received_date").defaultNow(),
  isLatest: boolean("is_latest").default(true),
  autoExtracted: boolean("auto_extracted").default(false), // Whether values were auto-extracted
  extractionConfidence: decimal("extraction_confidence", { precision: 5, scale: 2 }), // AI confidence score

  orgId: uuid("org_id").references(() => organizations.id, { onDelete: "cascade" }),

  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("idx_estimates_claim_id").on(table.claimId),
  index("idx_estimates_type").on(table.type),
]);


// Estimate Comparisons table - caches AI comparison results
export const estimateComparisons = pgTable("estimate_comparisons", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "cascade" }).notNull(),
  paEstimateId: uuid("pa_estimate_id").references(() => estimates.id, { onDelete: "cascade" }).notNull(),
  carrierEstimateId: uuid("carrier_estimate_id").references(() => estimates.id, { onDelete: "cascade" }).notNull(),
  comparisonResult: jsonb("comparison_result").notNull(), // Full EstimateComparisonResult
  recommendedAction: varchar("recommended_action").notNull(), // accept, negotiate, appraisal, attorney
  totalDifference: decimal("total_difference", { precision: 12, scale: 2 }),
  percentageDifference: decimal("percentage_difference", { precision: 5, scale: 2 }),
  confidence: decimal("confidence", { precision: 5, scale: 2 }),
  aiModel: varchar("ai_model"),
  aiTokensUsed: integer("ai_tokens_used"),
  aiInteractionId: varchar("ai_interaction_id"),
  runBy: varchar("run_by").references(() => users.id),

  orgId: uuid("org_id").references(() => organizations.id, { onDelete: "cascade" }),

  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("idx_estimate_comparisons_claim_id").on(table.claimId),
  index("idx_estimate_comparisons_pa_estimate").on(table.paEstimateId),
  index("idx_estimate_comparisons_carrier_estimate").on(table.carrierEstimateId),
]);


// Enhanced Carrier Offers table with comprehensive settlement details
export const carrierOffers = pgTable("carrier_offers", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "cascade" }).notNull(),
  offerType: offerTypeEnum("offer_type").notNull(),
  policyCoverage: policyCoverageEnum("policy_coverage").notNull(),
  description: text("description"), // Details of what offer was for
  status: varchar("status").default("accepted_offer"), // accepted_offer, rejected, pending, etc.
  
  // Offer details and dates
  offerDate: timestamp("offer_date").notNull(),
  settlementDate: timestamp("settlement_date"), // For tracking deadlines
  
  // Financial breakdown - Estimate section
  replacementCostValue: decimal("replacement_cost_value", { precision: 12, scale: 2 }).default('0'),
  nonRecoverableDepreciation: decimal("non_recoverable_depreciation", { precision: 12, scale: 2 }).default('0'),
  holdbackDepreciation: decimal("holdback_depreciation", { precision: 12, scale: 2 }).default('0'),
  actualCostValue: decimal("actual_cost_value", { precision: 12, scale: 2 }).default('0'),
  deductible: decimal("deductible", { precision: 12, scale: 2 }).default('0'),
  priorPayment: decimal("prior_payment", { precision: 12, scale: 2 }).default('0'),
  
  // Calculated fields
  netSettlement: decimal("net_settlement", { precision: 12, scale: 2 }).default('0'),
  
  // Metadata
  letterDocumentId: uuid("letter_document_id").references(() => documents.id),
  notes: text("notes"),
  isRecoverable: boolean("is_recoverable").default(true), // For holdback depreciation
  createdBy: varchar("created_by").references(() => users.id),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_carrier_offers_claim_id").on(table.claimId),
  index("idx_carrier_offers_offer_date").on(table.offerDate),
  index("idx_carrier_offers_type").on(table.offerType),
]);


// Enhanced Carrier Checks table with comprehensive payment tracking
export const carrierChecks = pgTable("carrier_checks", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "cascade" }).notNull(),
  
  // Payment details
  amount: decimal("amount", { precision: 12, scale: 2 }).notNull(),
  paymentType: paymentTypeEnum("payment_type").default("check"),
  checkReferenceNumber: varchar("check_reference_number"), // Check number or reference
  paymentName: varchar("payment_name"), // Name this payment
  notes: text("notes"), // Additional payment details
  
  // Processing status and dates
  processingStatus: paymentProcessingStatusEnum("processing_status").default("received"),
  dateIssued: timestamp("date_issued"),
  dateReceived: timestamp("date_received").defaultNow(),
  
  // Bank clearance tracking
  paymentCleared: boolean("payment_cleared").default(false), // Has payment cleared the bank
  paymentClearedAt: timestamp("payment_cleared_at"), // When it cleared
  clearedBy: varchar("cleared_by").references(() => users.id), // Who marked it cleared
  
  // Payment breakdown allocation
  checkTotal: decimal("check_total", { precision: 12, scale: 2 }).default('0'),
  amountAllocated: decimal("amount_allocated", { precision: 12, scale: 2 }).default('0'),
  remainingToAllocate: decimal("remaining_to_allocate", { precision: 12, scale: 2 }).default('0'),
  paidAmount: decimal("paid_amount", { precision: 12, scale: 2 }),

  // Track if check was received prior to MPLC
  receivedPriorToMPLC: boolean("received_prior_to_mplc").default(false),
  priorRecipient: varchar("prior_recipient"), // Who received it before MPLC
  priorReceivedDate: timestamp("prior_received_date"), // When they received it
  
  // Adjustable company fee percentage
  companyFeePercentage: decimal("company_fee_percentage", { precision: 5, scale: 2 }).default('100'), // Default 100%
  companyFeeAmount: decimal("company_fee_amount", { precision: 12, scale: 2 }), // Calculated company fee
  
  // Document attachments (front/back of check images)
  frontImageId: uuid("front_image_id").references(() => documents.id),
  backImageId: uuid("back_image_id").references(() => documents.id),
  
  // QuickBooks Online Integration
  qboPaymentId: varchar("qbo_payment_id"), // QBO Payment ID when synced
  qboSyncedAt: timestamp("qbo_synced_at"), // Last successful sync timestamp

  // Check Processing Desk (CPD) fields
  cpdStage: checkProcessingStageEnum("cpd_stage"), // NULL for checks not in CPD flow
  verificationChecklist: jsonb("verification_checklist"),
  iinkCheckType: varchar("iink_check_type"),
  iinkPropertyAddress: text("iink_property_address"),
  iinkMortgageeOnCheck: boolean("iink_mortgagee_on_check").default(false),
  iinkSignedByMortgagee: boolean("iink_signed_by_mortgagee").default(false),
  iinkPayees: jsonb("iink_payees"),
  iinkSubmittedAt: timestamp("iink_submitted_at"),
  step4Checklist: jsonb("step4_checklist"),

  // Metadata
  createdBy: varchar("created_by").references(() => users.id),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_carrier_checks_claim_id").on(table.claimId),
  index("idx_carrier_checks_date_received").on(table.dateReceived),
  index("idx_carrier_checks_processing_status").on(table.processingStatus),
]);


// Check Estimate Breakdowns table for CPD step 2
export const checkEstimateBreakdowns = pgTable("check_estimate_breakdowns", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  carrierCheckId: uuid("carrier_check_id").references(() => carrierChecks.id, { onDelete: "cascade" }).notNull(),
  coverageType: varchar("coverage_type").notNull(),
  description: varchar("description"),
  rcvAmount: decimal("rcv_amount", { precision: 12, scale: 2 }).default('0'),
  acvAmount: decimal("acv_amount", { precision: 12, scale: 2 }).default('0'),
  depreciationAmount: decimal("depreciation_amount", { precision: 12, scale: 2 }).default('0'),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_check_estimate_breakdowns_check_id").on(table.carrierCheckId),
]);


// Payment Coverage Breakdown table for allocating payments across coverages
export const paymentCoverageBreakdowns = pgTable("payment_coverage_breakdowns", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  carrierCheckId: uuid("carrier_check_id").references(() => carrierChecks.id, { onDelete: "cascade" }).notNull(),
  coverage: policyCoverageEnum("coverage").notNull(),
  netAmount: decimal("net_amount", { precision: 12, scale: 2 }).notNull(),
  recoverableDepreciation: decimal("recoverable_depreciation", { precision: 12, scale: 2 }).default('0'),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("idx_payment_coverage_breakdowns_check_id").on(table.carrierCheckId),
]);


// Legacy Payments table (kept for backward compatibility)
export const payments = pgTable("payments", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "cascade" }).notNull(),
  payer: varchar("payer").notNull(), // Carrier name or other payer
  amount: decimal("amount", { precision: 12, scale: 2 }).notNull(),
  paymentDate: timestamp("payment_date").notNull(),
  checkNumber: varchar("check_number"),
  memo: text("memo"),
  status: paymentStatusEnum("status").default("pending"),
  documentId: uuid("document_id").references(() => documents.id),

  orgId: uuid("org_id").references(() => organizations.id, { onDelete: "cascade" }),

  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("idx_payments_claim_id").on(table.claimId),
  index("idx_payments_date").on(table.paymentDate),
]);


// Invoices table
export const invoices = pgTable("invoices", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "cascade" }).notNull(),
  invoiceNumber: varchar("invoice_number").unique().notNull(),
  qbInvoiceId: varchar("qb_invoice_id"), // QuickBooks integration
  amount: decimal("amount", { precision: 12, scale: 2 }).notNull(),
  feePercentage: decimal("fee_percentage", { precision: 5, scale: 2 }),
  status: invoiceStatusEnum("status").default("draft"),
  issuedAt: timestamp("issued_at"),
  dueAt: timestamp("due_at"),
  paidAt: timestamp("paid_at"),
  notes: text("notes"),
  createdBy: varchar("created_by").references(() => users.id).notNull(),

  orgId: uuid("org_id").references(() => organizations.id, { onDelete: "cascade" }),

  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_invoices_claim_id").on(table.claimId),
  index("idx_invoices_status").on(table.status),
]);


// Invoice Line Items table for detailed invoice breakdown
export const invoiceLineItems = pgTable("invoice_line_items", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  invoiceId: uuid("invoice_id").references(() => invoices.id, { onDelete: "cascade" }).notNull(),
  name: varchar("name").notNull(), // Service fee, item name, etc.
  description: text("description"),
  price: decimal("price", { precision: 12, scale: 2 }).notNull(),
  quantity: decimal("quantity", { precision: 10, scale: 2 }).default('1'),
  total: decimal("total", { precision: 12, scale: 2 }).notNull(),
  sortOrder: integer("sort_order").default(0),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("idx_invoice_line_items_invoice_id").on(table.invoiceId),
]);


// Receivable Fees table for comprehensive fee management
export const receivableFees = pgTable("receivable_fees", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "cascade" }).notNull(),
  
  // Fee configuration
  feeType: feeTypeEnum("fee_type").notNull(),
  feePercentage: decimal("fee_percentage", { precision: 5, scale: 2 }), // For percentage fees
  flatAmount: decimal("flat_amount", { precision: 12, scale: 2 }), // For flat rate fees
  hourlyRate: decimal("hourly_rate", { precision: 8, scale: 2 }), // For hourly fees
  hoursWorked: decimal("hours_worked", { precision: 8, scale: 2 }), // For time-based fees
  
  // Fee calculation
  feeSource: feeSourceEnum("fee_source").notNull(),
  calculationBasis: decimal("calculation_basis", { precision: 12, scale: 2 }), // Amount fee is calculated on
  calculatedTotal: decimal("calculated_total", { precision: 12, scale: 2 }).notNull(),
  
  // Fee details and metadata
  description: varchar("description").notNull(),
  referenceNumber: varchar("reference_number"), // For tracking purposes
  dateIncurred: timestamp("date_incurred").notNull(),
  notes: text("notes"),
  
  // Payment tracking
  isPaid: boolean("is_paid").default(false),
  paidAmount: decimal("paid_amount", { precision: 12, scale: 2 }).default('0'),
  paidDate: timestamp("paid_date"),
  
  createdBy: varchar("created_by").references(() => users.id),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_receivable_fees_claim_id").on(table.claimId),
  index("idx_receivable_fees_date_incurred").on(table.dateIncurred),
  index("idx_receivable_fees_is_paid").on(table.isPaid),
]);


// Company Expenses table for tracking business expenses
export const companyExpenses = pgTable("company_expenses", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "cascade" }), // Optional - may not be claim-specific
  
  // Expense details
  expenseType: expenseTypeEnum("expense_type").notNull(),
  vendor: varchar("vendor"), // Company/person expense was paid to
  description: text("description").notNull(),
  amount: decimal("amount", { precision: 12, scale: 2 }).notNull(),
  
  // Dates and tracking
  expenseDate: timestamp("expense_date").notNull(),
  dueDate: timestamp("due_date"),
  paidDate: timestamp("paid_date"),
  
  // Payment status
  isPaid: boolean("is_paid").default(false),
  paymentMethod: varchar("payment_method"), // check, card, transfer, etc.
  receiptDocumentId: uuid("receipt_document_id").references(() => documents.id),
  
  // Categorization
  category: varchar("category"), // Office supplies, travel, professional services, etc.
  isReimbursable: boolean("is_reimbursable").default(false),
  reimbursedAmount: decimal("reimbursed_amount", { precision: 12, scale: 2 }).default('0'),
  
  notes: text("notes"),
  createdBy: varchar("created_by").references(() => users.id),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_company_expenses_claim_id").on(table.claimId),
  index("idx_company_expenses_expense_date").on(table.expenseDate),
  index("idx_company_expenses_vendor").on(table.vendor),
  index("idx_company_expenses_is_paid").on(table.isPaid),
]);


// Staff Time table for tracking time worked on claims
export const staffTime = pgTable("staff_time", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "cascade" }).notNull(),
  userId: varchar("user_id").references(() => users.id).notNull(),
  
  // Time entry details
  entryType: timeEntryTypeEnum("entry_type").notNull(),
  description: text("description").notNull(),
  hoursWorked: decimal("hours_worked", { precision: 8, scale: 2 }).notNull(),
  
  // Dates
  workDate: timestamp("work_date").notNull(),
  startTime: timestamp("start_time"),
  endTime: timestamp("end_time"),
  
  // Billing information
  hourlyRate: decimal("hourly_rate", { precision: 8, scale: 2 }),
  billableAmount: decimal("billable_amount", { precision: 12, scale: 2 }),
  isBillable: boolean("is_billable").default(true),
  isBilled: boolean("is_billed").default(false),
  billedDate: timestamp("billed_date"),
  
  // Approval workflow
  isApproved: boolean("is_approved").default(false),
  approvedBy: varchar("approved_by").references(() => users.id),
  approvedAt: timestamp("approved_at"),
  
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_staff_time_claim_id").on(table.claimId),
  index("idx_staff_time_user_id").on(table.userId),
  index("idx_staff_time_work_date").on(table.workDate),
  index("idx_staff_time_is_billable").on(table.isBillable),
]);


// Commissions table
export const commissions = pgTable("commissions", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "cascade" }).notNull(),
  userId: varchar("user_id").references(() => users.id).notNull(),
  carrierCheckId: uuid("carrier_check_id").references(() => carrierChecks.id), // Link to carrier check
  
  // Commission details
  dutyType: varchar("duty_type").notNull(), // appraisal, field_adjusting, sales, desk_adjusting, referral_fee
  roleType: varchar("role_type").notNull(), // Role when commission was earned
  adjusterLevel: varchar("adjuster_level"), // regular, senior, director (at time of commission)
  
  // Financial details
  checkAmount: decimal("check_amount", { precision: 12, scale: 2 }).notNull(), // Total check amount
  companyFeeAmount: decimal("company_fee_amount", { precision: 12, scale: 2 }).notNull(), // Company's fee from check
  calculationBasis: decimal("calculation_basis", { precision: 12, scale: 2 }).notNull(), // Base amount used for calculating commission
  percentage: decimal("percentage", { precision: 5, scale: 2 }).notNull(), // Percentage for this adjuster
  amount: decimal("amount", { precision: 12, scale: 2 }).notNull(), // Calculated commission amount
  
  // Payment tracking
  commissionPaymentId: uuid("commission_payment_id"), // Link to commission payment (forward reference, added after commissionPayments table)
  isPaid: boolean("is_paid").default(false),
  paidAt: timestamp("paid_at"),
  paymentMethod: varchar("payment_method"), // check, ach, wire - deprecated, use commissionPayments.paymentMethod
  
  // QuickBooks Online Integration - Commission as Bill line item
  qboBillId: varchar("qbo_bill_id"), // QBO Bill ID when synced as expense
  qboSyncedAt: timestamp("qbo_synced_at"), // Last successful sync timestamp
  
  // Metadata
  calculationNotes: text("calculation_notes"), // Explanation of how commission was calculated
  notes: text("notes"),
  createdBy: varchar("created_by").references(() => users.id),
  approvedBy: varchar("approved_by").references(() => users.id),
  approvedAt: timestamp("approved_at"),

  orgId: uuid("org_id").references(() => organizations.id, { onDelete: "cascade" }),

  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_commissions_claim_id").on(table.claimId),
  index("idx_commissions_user_id").on(table.userId),
  index("idx_commissions_check_id").on(table.carrierCheckId),
  index("idx_commissions_payment_id").on(table.commissionPaymentId),
]);


// Commission Payments table - tracks payments issued to adjusters for their commissions
export const commissionPayments = pgTable("commission_payments", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  
  // Payment recipient
  userId: varchar("user_id").references(() => users.id).notNull(), // Adjuster receiving payment
  
  // Payment details
  totalAmount: decimal("total_amount", { precision: 12, scale: 2 }).notNull(), // Total payment amount
  paymentMethod: varchar("payment_method").notNull(), // check, ach, wire, direct_deposit
  paymentDate: timestamp("payment_date").notNull(),
  checkNumber: varchar("check_number"), // For check payments
  referenceNumber: varchar("reference_number"), // For electronic payments
  
  // Status tracking
  status: varchar("status").default("pending").notNull(), // pending, issued, cleared, cancelled
  issuedAt: timestamp("issued_at"),
  clearedAt: timestamp("cleared_at"),
  
  // Batch tracking
  batchId: varchar("batch_id"), // Group multiple payments for batch processing
  batchName: varchar("batch_name"), // Optional name for the batch
  
  // QuickBooks Online Integration
  qboBillPaymentId: varchar("qbo_bill_payment_id"), // QBO BillPayment ID when synced
  qboSyncedAt: timestamp("qbo_synced_at"), // Last successful sync timestamp
  
  // Metadata
  notes: text("notes"),
  createdBy: varchar("created_by").references(() => users.id).notNull(),
  approvedBy: varchar("approved_by").references(() => users.id),
  approvedAt: timestamp("approved_at"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_commission_payments_user_id").on(table.userId),
  index("idx_commission_payments_batch_id").on(table.batchId),
  index("idx_commission_payments_status").on(table.status),
  index("idx_commission_payments_payment_date").on(table.paymentDate),
]);


// Estimate Learning Library - for teaching AI about carrier estimate discrepancies
export const estimatePitfallPatterns = pgTable("estimate_pitfall_patterns", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  
  // Pattern identification
  name: varchar("name", { length: 255 }).notNull(), // e.g., "Depreciation on Non-Depreciable Items"
  category: varchar("category").notNull(), // undervaluation, missing_items, incorrect_pricing, scope_gap, calculation_error
  severity: varchar("severity").default("medium"), // low, medium, high, critical
  
  // Pattern description
  description: text("description").notNull(), // What this pitfall is
  howToSpot: text("how_to_spot"), // How to identify it in an estimate
  typicalImpact: text("typical_impact"), // Financial impact / how much money is left on table
  remediation: text("remediation"), // How to address it in negotiations
  
  // Metadata
  applicablePerilTypes: jsonb("applicable_peril_types"), // Array of peril types where this applies
  applicableStates: jsonb("applicable_states"), // States where this is common
  carrierTendencies: jsonb("carrier_tendencies"), // Which carriers use this tactic
  
  // Usage tracking
  timesIdentified: integer("times_identified").default(0),
  averageRecoveryAmount: decimal("average_recovery_amount", { precision: 12, scale: 2 }),
  
  isActive: boolean("is_active").default(true),
  createdBy: varchar("created_by").references(() => users.id),

  orgId: uuid("org_id").references(() => organizations.id, { onDelete: "cascade" }),

  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_estimate_patterns_category").on(table.category),
  index("idx_estimate_patterns_severity").on(table.severity),
]);


export const estimateSamples = pgTable("estimate_samples", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  
  // Association
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "set null" }), // Optional - may not always have claim context
  
  // Estimate documents
  carrierEstimateDocId: uuid("carrier_estimate_doc_id").references(() => documents.id),
  paEstimateDocId: uuid("pa_estimate_doc_id").references(() => documents.id),
  
  // Estimate metadata
  perilType: varchar("peril_type"), // Type of loss
  propertyType: varchar("property_type"), // residential, commercial
  propertyState: varchar("property_state", { length: 2 }),
  carrierName: varchar("carrier_name"),
  
  // Value comparison
  carrierEstimateAmount: decimal("carrier_estimate_amount", { precision: 12, scale: 2 }),
  paEstimateAmount: decimal("pa_estimate_amount", { precision: 12, scale: 2 }),
  recoveredAmount: decimal("recovered_amount", { precision: 12, scale: 2 }), // What was actually recovered
  recoveryPercentage: decimal("recovery_percentage", { precision: 5, scale: 2 }), // % of PA estimate achieved
  
  // Context and outcomes
  expertCommentary: text("expert_commentary"), // Overall analysis from adjuster
  negotiationNotes: text("negotiation_notes"), // What worked in negotiations
  finalOutcome: varchar("final_outcome"), // settled, appraisal, litigation, etc.
  
  // Learning status
  isApprovedForTraining: boolean("is_approved_for_training").default(false),
  approvedBy: varchar("approved_by").references(() => users.id),
  approvedAt: timestamp("approved_at"),
  linkedLearningExampleId: uuid("linked_learning_example_id").references(() => learningExamples.id),
  
  // Metadata
  uploadedBy: varchar("uploaded_by").references(() => users.id).notNull(),

  orgId: uuid("org_id").references(() => organizations.id, { onDelete: "cascade" }),

  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_estimate_samples_claim").on(table.claimId),
  index("idx_estimate_samples_approved").on(table.isApprovedForTraining),
  index("idx_estimate_samples_peril").on(table.perilType),
]);


// Fee Confirmations - Track fee approval workflow
export const feeConfirmations = pgTable("fee_confirmations", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  
  // Claim reference
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "cascade" }).notNull(),
  
  // Fee configuration
  feePercentage: decimal("fee_percentage", { precision: 5, scale: 2 }).notNull(), // e.g., 10.00 for 10%
  feeBasis: feeBasisEnum("fee_basis").notNull().default("entire_claim"),
  
  // Exclusions (what's NOT included in fee calculation)
  excludeAle: boolean("exclude_ale").default(true), // Exclude Additional Living Expenses
  excludeDeductible: boolean("exclude_deductible").default(true), // Exclude deductible
  excludeContents: boolean("exclude_contents").default(false), // Optional: exclude contents coverage
  customExclusions: jsonb("custom_exclusions"), // Array of custom exclusion items
  
  // Loss location confirmation
  lossAddressConfirmed: boolean("loss_address_confirmed").default(false),
  lossAddressConfirmedAt: timestamp("loss_address_confirmed_at"),
  lossAddress: text("loss_address"), // Snapshot of confirmed address
  
  // Fee amount estimates
  estimatedClaimValue: decimal("estimated_claim_value", { precision: 12, scale: 2 }),
  estimatedFeeAmount: decimal("estimated_fee_amount", { precision: 12, scale: 2 }),
  
  // Approval workflow
  confirmedAt: timestamp("confirmed_at"),
  confirmedBy: varchar("confirmed_by").references(() => users.id),
  
  // Status
  status: varchar("status").default("pending"), // pending, confirmed, modified, cancelled
  
  createdBy: varchar("created_by").references(() => users.id).notNull(),

  orgId: uuid("org_id").references(() => organizations.id, { onDelete: "cascade" }),

  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_fee_confirmations_claim_id").on(table.claimId),
  index("idx_fee_confirmations_status").on(table.status),
  index("idx_fee_confirmations_confirmed_at").on(table.confirmedAt),
]);


// Estimate Component Groups - Learn which estimate items commonly go together
export const estimateComponentGroups = pgTable("estimate_component_groups", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  
  // Group identification
  groupName: varchar("group_name").notNull(),
  category: varchar("category").notNull(),
  perilType: varchar("peril_type"),
  
  // Core components
  coreItems: jsonb("core_items").notNull(),
  optionalItems: jsonb("optional_items"),
  frequentlyMissedItems: jsonb("frequently_missed_items"),
  
  // Pricing patterns
  typicalPriceRange: jsonb("typical_price_range"),
  priceRatios: jsonb("price_ratios"),
  
  // Usage statistics
  timesObserved: integer("times_observed").default(0),
  successRate: decimal("success_rate", { precision: 5, scale: 2 }),
  averageRecoveryIncrease: decimal("average_recovery_increase", { precision: 12, scale: 2 }),
  
  // Example claims
  exampleClaimIds: jsonb("example_claim_ids"),
  
  // Status
  isActive: boolean("is_active").default(true),
  isVerified: boolean("is_verified").default(false),
  verifiedBy: varchar("verified_by").references(() => users.id),
  verifiedAt: timestamp("verified_at"),
  
  createdBy: varchar("created_by").references(() => users.id),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_estimate_component_groups_category").on(table.category),
  index("idx_estimate_component_groups_peril").on(table.perilType),
  index("idx_estimate_component_groups_active").on(table.isActive),
]);


// Estimate Item Observations - Track individual item occurrences for pattern learning
export const estimateItemObservations = pgTable("estimate_item_observations", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  
  // Source
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "cascade" }).notNull(),
  documentId: uuid("document_id").references(() => documents.id, { onDelete: "set null" }),
  estimateSource: varchar("estimate_source").notNull(),
  
  // Item details
  itemCode: varchar("item_code"),
  itemDescription: text("item_description").notNull(),
  category: varchar("category").notNull(),
  
  // Values
  quantity: decimal("quantity", { precision: 12, scale: 2 }),
  unit: varchar("unit"),
  unitPrice: decimal("unit_price", { precision: 12, scale: 2 }),
  totalPrice: decimal("total_price", { precision: 12, scale: 2 }),
  
  // Related items (items that appeared together)
  coOccurringItems: jsonb("co_occurring_items"),
  parentItem: varchar("parent_item"),
  
  // Outcome tracking
  wasIncludedInFinal: boolean("was_included_in_final"),
  wasDisputed: boolean("was_disputed"),
  disputeOutcome: varchar("dispute_outcome"),
  
  perilType: varchar("peril_type"),
  propertyType: varchar("property_type"),
  state: varchar("state", { length: 2 }),
  carrier: varchar("carrier"),
  
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("idx_estimate_item_obs_claim").on(table.claimId),
  index("idx_estimate_item_obs_category").on(table.category),
  index("idx_estimate_item_obs_peril").on(table.perilType),
  index("idx_estimate_item_obs_item_code").on(table.itemCode),
]);
